import { MaintenanceStatus, PaymentStatus, MaintenanceReturnReason } from './types';
import React from 'react';
import {
  Schedule as PendingIcon,
  Build as InProgressIcon,
  CheckCircle as CompletedIcon,
  DoneAll as DeliveredIcon,
  Cancel as CancelledIcon,
  Undo as RefundedIcon,
  AssignmentReturn as ReturnedIcon
} from '@mui/icons-material';

// حالات طلبات الصيانة مع الألوان
export const statusColors: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'info',
  [MaintenanceStatus.IN_PROGRESS]: 'info',
  [MaintenanceStatus.COMPLETED]: 'warning',
  [MaintenanceStatus.DELIVERED]: 'success',
  [MaintenanceStatus.CANCELLED]: 'error',
  [MaintenanceStatus.REFUNDED]: 'error',
  [MaintenanceStatus.RETURNED]: 'warning'
};

// ترجمة حالات الصيانة
export const statusLabels: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'قيد الانتظار',
  [MaintenanceStatus.IN_PROGRESS]: 'جاري العمل',
  [MaintenanceStatus.COMPLETED]: 'تم الإصلاح',
  [MaintenanceStatus.DELIVERED]: 'تم التسليم',
  [MaintenanceStatus.CANCELLED]: 'مرتجع',
  [MaintenanceStatus.REFUNDED]: 'مرتجع مع استرداد',
  [MaintenanceStatus.RETURNED]: 'مرتجع مع رفض الجهاز'
};

// تلميحات حالات الصيانة (tooltips)
export const statusTooltips: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'الطلب في انتظار بدء الصيانة',
  [MaintenanceStatus.IN_PROGRESS]: 'جاري العمل على إصلاح الجهاز',
  [MaintenanceStatus.COMPLETED]: 'تم إصلاح الجهاز وهو جاهز للتسليم',
  [MaintenanceStatus.DELIVERED]: 'تم تسليم الجهاز للعميل',
  [MaintenanceStatus.CANCELLED]: 'في انتظار الصيانة - تم إرجاع الجهاز قبل البدء',
  [MaintenanceStatus.REFUNDED]: 'في فترة ضمان الصيانة - تم إرجاع الجهاز مع استرداد المبلغ',
  [MaintenanceStatus.RETURNED]: 'تعدى مدة الضمان - تم إرجاع الجهاز مع رفض الإصلاح'
};

// أيقونات حالات الصيانة
export const statusIcons: Record<MaintenanceStatus, React.ReactElement> = {
  [MaintenanceStatus.PENDING]: React.createElement(PendingIcon),
  [MaintenanceStatus.IN_PROGRESS]: React.createElement(InProgressIcon),
  [MaintenanceStatus.COMPLETED]: React.createElement(CompletedIcon),
  [MaintenanceStatus.DELIVERED]: React.createElement(DeliveredIcon),
  [MaintenanceStatus.CANCELLED]: React.createElement(CancelledIcon),
  [MaintenanceStatus.REFUNDED]: React.createElement(RefundedIcon),
  [MaintenanceStatus.RETURNED]: React.createElement(ReturnedIcon)
};

// حالات الدفع مع الألوان
export const paymentStatusColors: Record<PaymentStatus, string> = {
  'unpaid': 'error',
  'partiallyPaid': 'warning',
  'fullyPaid': 'success',
  'paid': 'success',
  'partial': 'warning'
};

// ترجمة حالات الدفع
export const paymentStatusLabels: Record<PaymentStatus, string> = {
  'unpaid': 'غير مدفوع',
  'partiallyPaid': 'مدفوع جزئيًا',
  'fullyPaid': 'مدفوع بالكامل',
  'paid': 'مدفوع',
  'partial': 'مدفوع جزئيًا'
};

// ترجمة أسباب إرجاع الصيانة
export const returnReasonLabels: Record<MaintenanceReturnReason, string> = {
  [MaintenanceReturnReason.SAME_ISSUE]: 'نفس العيب',
  [MaintenanceReturnReason.NEW_ISSUE]: 'عيب جديد',
  [MaintenanceReturnReason.CUSTOMER_REFUSAL]: 'رفض العميل للإصلاح',
  [MaintenanceReturnReason.COST_TOO_HIGH]: 'التكلفة عالية',
  [MaintenanceReturnReason.PART_NOT_AVAILABLE]: 'قطعة غير متوفرة',
  [MaintenanceReturnReason.DEVICE_BEYOND_REPAIR]: 'الجهاز غير قابل للإصلاح',
  [MaintenanceReturnReason.OTHER]: 'أخرى'
};

// أنواع الأجهزة المتاحة
export const deviceTypes = [
  'هاتف ذكي',
  'جهاز لوحي (تابلت)',
  'كمبيوتر محمول',
  'كمبيوتر مكتبي',
  'طابعة',
  'شاشة',
  'جهاز ألعاب',
  'مكيف هواء',
  'ثلاجة',
  'غسالة',
  'تلفاز',
  'أخرى'
];

// طرق الدفع المتاحة
export const paymentMethods = [
  { value: 'cash', label: 'نقدي' },
  { value: 'card', label: 'بطاقة ائتمان' },
  { value: 'bankTransfer', label: 'تحويل بنكي' },
  { value: 'stc', label: 'STC Pay' },
  { value: 'mada', label: 'مدى' }
];

// العملات المتاحة
export const currencies = [
  { value: 'EGP', label: 'جنيه مصري', symbol: 'ج.م' },
  { value: 'SAR', label: 'ريال سعودي', symbol: 'ر.س' },
  { value: 'USD', label: 'دولار أمريكي', symbol: '$' },
  { value: 'EUR', label: 'يورو', symbol: '€' }
];

// العملة الافتراضية
export const DEFAULT_CURRENCY = 'EGP';
export const DEFAULT_CURRENCY_SYMBOL = 'ج.م';

/**
 * تنسيق العملة (استخدم useCurrency hook بدلاً من هذه الدالة)
 * @param amount المبلغ
 * @returns المبلغ المنسق
 * @deprecated استخدم useCurrency hook بدلاً من هذه الدالة
 */
export const currencyFormat = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) {
    return `0.00 ${DEFAULT_CURRENCY_SYMBOL}`;
  }
  return `${amount.toFixed(2)} ${DEFAULT_CURRENCY_SYMBOL}`;
};